/**
 * Service worker registration for MusicBooth.
 *
 * The PWA install affordance (Chrome's "Install app" banner, iOS "Add to
 * Home Screen") is only useful to the HOST/DJ, never to a guest scanning
 * a QR code. We therefore:
 *
 *   1. Skip SW registration entirely on guest paths.
 *   2. Strip the <link rel="manifest"> so browsers don't surface the install
 *      prompt purely off the manifest's `display: standalone`.
 *   3. Unregister any previously-registered SW + clear its caches when a
 *      guest revisits — handles the case where the host installed it once
 *      then handed the device to a customer.
 *
 * On host routes, we register a network-first SW (see service-worker.js).
 *
 * ============== Cache-freshness contract (Feb 2026) ==============
 * Field-reported symptom: "changes not applying at client locations."
 * Root cause: venue tablets stay on the same tab for days/weeks, so the
 * browser never bothers to re-check for a new service-worker.js from
 * the network — the tab keeps running whichever build was cached the
 * first day the tablet booted.
 *
 * Three-layer fix:
 *   (a) `updateViaCache: 'none'` on register() — the browser is FORBIDDEN
 *       from serving service-worker.js from HTTP cache. Every navigation
 *       triggers a fresh SW file fetch, so a new deploy propagates within
 *       the browser's SW-file staleness heuristic (usually seconds).
 *   (b) `reg.update()` on load + every 30 minutes — proactively pokes the
 *       browser to check for a new SW file even when the tab isn't
 *       navigating (venue tablets = same tab for hours).
 *   (c) Weekly hard-reset — localStorage-timestamped force-purge of all
 *       caches + SW unregister + one reload. Guarantees no venue is
 *       running >7-day-old JS regardless of network flakiness.
 */

const SW_UPDATE_INTERVAL_MS = 30 * 60 * 1000;      // (b) poke for new SW every 30 min
// (c) Auto cache reset — reduced from 7d → 12h in response to field reports
// that after 4-5h continuous playback the prewarm handshake starts lagging
// (likely accumulated JS heap / stale Apple Music session state on venue
// tablets). 12h means a nightly / mid-morning refresh cycle for most venues.
// KEY BUMP: renamed the localStorage key so existing "7d ago" stamps don't
// suppress the first 12h reset after the rename lands.
//
// TIERED ESCALATION (Feb 2026) — venues that play continuously (bars, clubs)
// never present a natural "no audio" window, so the safety gate kept
// deferring the reset indefinitely. Symptom in the field: after ~36h the
// tab is sluggish and route transitions lag. Three thresholds:
//   • 12h  → prefer full idle (audio must be paused). Deferred if live.
//   • 36h  → escalated: wait up to 6s for ANY momentary lull in audio
//            (e.g. the ~1-3s gap Apple MusicKit leaves between tracks
//            when true crossfading isn't possible under FairPlay). If a
//            lull is detected within the window, reset now.
//   • 40h  → forced: reset immediately regardless of audio state. Clips
//            at most a few seconds of the current track — a small price
//            for a fresh app after ~1.7 days continuous use.
const AUTO_RESET_INTERVAL_MS = 12 * 3600 * 1000;
const AUTO_RESET_ESCALATE_MS = 36 * 3600 * 1000;
const AUTO_RESET_FORCE_MS    = 40 * 3600 * 1000;
const AUTO_RESET_LULL_WAIT_MS = 6 * 1000;    // how long to wait for a lull at the 36h tier
const AUTO_RESET_LULL_POLL_MS = 150;         // how often to poll the live flag while waiting
const AUTO_RESET_DEFER_INTERVAL_MS = 5 * 60 * 1000; // if audio is live, retry in 5 min
const AUTO_RESET_KEY = "spin:sw:lastCacheClearAt:v2";

const GUEST_PATH_PATTERNS = [
  /^\/v\//,           // /v/:venueCode  → QR landing
  /^\/request\//,     // /request/:code → song-request page
  /^\/menu\//,
  /^\/review\//,
  /^\/play\//,
  /^\/tv\//,
  /\/tv$/,            // /:venueCode/tv
];

function isGuestPath() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "/";
  return GUEST_PATH_PATTERNS.some((re) => re.test(path));
}

function stripManifestLink() {
  if (typeof document === "undefined") return;
  document.querySelectorAll('link[rel="manifest"]').forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  // Also drop apple-touch / theme-color tags that nudge "Add to Home Screen"
  // on iOS Safari. These are still useful for hosts — but on guest pages
  // they make customers think the venue is asking them to install something.
  document
    .querySelectorAll('meta[name="apple-mobile-web-app-capable"], meta[name="mobile-web-app-capable"]')
    .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
}

async function unregisterAllSWs() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
  } catch {
    /* noop */
  }
  // Clear cached app-shell + assets so the guest tab is a vanilla page load.
  if (typeof caches !== "undefined") {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    } catch {
      /* noop */
    }
  }
}

/**
 * Tiered auto-reset: after enough time has elapsed since the last purge,
 * wipe the SW cache + reload — but escalate the safety gate progressively
 * so a venue playing music non-stop still gets refreshed within ~1.7 days.
 *
 *   • 12h  — full idle only. If audio is live, defer 5 min.
 *   • 36h  — wait ~6s for ANY brief lull (crossfade gap, track transition).
 *            Fires as soon as `window.__spinItUpIsLive` reads false.
 *   • 40h  — unconditional. Clips at most a few seconds of the current
 *            track. Better than a sluggish 40h-old JS heap.
 *
 * The `__spinItUpIsLive` flag is set by MusicContext:
 *   window.__spinItUpIsLive = !!(isPlaying || crossfadeActive);
 *
 * Returns `true` if a reset was performed (caller should NOT continue
 * with normal SW registration — the reload will fire the whole flow
 * again with fresh state).
 */

async function _waitForBriefLull(maxWaitMs) {
  // Poll `window.__spinItUpIsLive` for a brief moment when it reads false.
  // Returns true if a lull was detected within `maxWaitMs`, false otherwise.
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    if (typeof window !== "undefined" && window.__spinItUpIsLive === false) {
      return true;
    }
    await new Promise((r) => setTimeout(r, AUTO_RESET_LULL_POLL_MS));
  }
  return false;
}

async function performAutoCacheResetIfDue() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return false;
  const now = Date.now();
  let last = 0;
  try {
    const raw = localStorage.getItem(AUTO_RESET_KEY);
    last = raw ? parseInt(raw, 10) : 0;
    if (Number.isNaN(last)) last = 0;
  } catch {
    last = 0;
  }
  // First-run visitors: stamp NOW but don't reload — no stale cache to purge yet.
  if (!last) {
    try { localStorage.setItem(AUTO_RESET_KEY, String(now)); } catch { /* noop */ }
    return false;
  }
  const elapsed = now - last;
  if (elapsed < AUTO_RESET_INTERVAL_MS) return false;
  // Also skip if the URL already has ?fresh= — prevents a reload loop if the
  // purge fails and the timestamp doesn't update.
  if (window.location.search.includes("fresh=")) {
    try { localStorage.setItem(AUTO_RESET_KEY, String(now)); } catch { /* noop */ }
    return false;
  }

  const forced = elapsed >= AUTO_RESET_FORCE_MS;
  const escalated = elapsed >= AUTO_RESET_ESCALATE_MS;
  const isLive = window.__spinItUpIsLive === true;

  if (isLive && !forced) {
    if (escalated) {
      // 36h+ — wait up to 6s for a brief lull in playback (crossfade gap,
      // track transition). If none arrives, defer to the next 5-min tick.
      const lull = await _waitForBriefLull(AUTO_RESET_LULL_WAIT_MS);
      if (!lull) {
        console.info("[SW] escalated auto-reset waited for a lull, none in", AUTO_RESET_LULL_WAIT_MS, "ms — deferring 5 min");
        return false;
      }
      console.info("[SW] escalated auto-reset — lull detected, wiping caches");
    } else {
      // 12h-36h — standard safety gate. Wait for real idle.
      console.info("[SW] auto-reset due but playback is live — deferring 5 min");
      return false;
    }
  } else if (forced && isLive) {
    console.info("[SW] force auto-reset at", Math.round(elapsed / 3600000), "h — clipping current track");
  } else {
    console.info("[SW] 12h auto-reset — wiping caches + reloading");
  }

  await unregisterAllSWs();
  try { localStorage.setItem(AUTO_RESET_KEY, String(now)); } catch { /* noop */ }
  const sep = window.location.search ? "&" : "?";
  window.location.replace(`${window.location.pathname}${window.location.search}${sep}fresh=${now}${window.location.hash}`);
  return true;
}

// Some browsers (Chrome desktop especially) fire a `beforeinstallprompt`
// event whenever the page meets installability criteria. We hard-suppress
// it on guest paths in case the manifest strip happens after install
// detection has already fired.
function blockInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
  });
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;

  if (isGuestPath()) {
    stripManifestLink();
    blockInstallPrompt();
    // Best-effort cleanup — don't block the page render on it.
    unregisterAllSWs();
    return;
  }

  if (!("serviceWorker" in navigator)) return;

  // (c) 12h auto-reset — must run BEFORE registration so we don't
  // register a SW just to immediately unregister it. If a reset fires,
  // the page reload will re-enter this whole function with fresh state.
  performAutoCacheResetIfDue().then((didReset) => {
    if (didReset) return; // reload in flight — no further work here

    // Periodic re-check: if the reset was DUE but deferred because audio
    // was live (or if we simply crossed the 12h mark during this session),
    // re-attempt on a 5-min interval. Cheap — mostly a no-op check.
    setInterval(() => {
      performAutoCacheResetIfDue().catch(() => { /* noop */ });
    }, AUTO_RESET_DEFER_INTERVAL_MS);

    // Defer registration until after the app paints so we don't block startup.
    window.addEventListener("load", () => {
      navigator.serviceWorker
        // `updateViaCache: 'none'` — (a) never serve service-worker.js from
        // HTTP cache. Ensures every SW-file fetch hits the network so new
        // deploys propagate immediately instead of waiting on browser's
        // ~24h SW-cache heuristic.
        .register("/service-worker.js", { scope: "/", updateViaCache: "none" })
        .then((reg) => {
          if (reg && reg.waiting) {
            reg.waiting.postMessage({ type: "SKIP_WAITING" });
          }
          reg.addEventListener("updatefound", () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener("statechange", () => {
              if (nw.state === "installed" && navigator.serviceWorker.controller) {
                nw.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });

          // (b) Proactively poke the browser to re-check for a new SW file
          // every 30 minutes. Handles the venue-tablet case where the tab
          // stays open for days — browser's own SW-update heuristic can
          // otherwise miss deploys entirely.
          setInterval(() => {
            reg.update().catch(() => { /* offline — try again next tick */ });
          }, SW_UPDATE_INTERVAL_MS);

          // Also poke immediately on tab-focus so a DJ tabbing back to the
          // console after checking Instagram picks up the new build.
          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
              reg.update().catch(() => { /* noop */ });
            }
          });
        })
        .catch((err) => {
          console.warn("[SW] registration failed:", err);
        });

      let reloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloaded) return;
        reloaded = true;
        // Auto-reload so users immediately get the new build instead of
        // running stale cached JS (which was causing "Video unavailable —
        // skipping" toasts from the deprecated YouTube engine).
        window.location.reload();
      });
    });
  });
}

/**
 * Public helper — venue admin can trigger a full cache wipe on demand.
 * Wired to the "Reset app cache" button in Settings so on-site staff can
 * force-refresh without waiting for the 12h timer.
 */
export async function forceResetAppCache() {
  await unregisterAllSWs();
  try { localStorage.setItem(AUTO_RESET_KEY, String(Date.now())); } catch { /* noop */ }
  const sep = window.location.search ? "&" : "?";
  window.location.replace(`${window.location.pathname}${window.location.search}${sep}fresh=${Date.now()}${window.location.hash}`);
}
